import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AudienceCriteria } from "@wcp/shared";
import { apiFetch } from "../../lib/api.js";

export type Segment = "new" | "repeat" | "vip";

export interface ContactRow {
  id: number;
  /** Operator-set custom name; display falls back to wa_name then phone. */
  name: string | null;
  /** The customer's own WhatsApp profile (push) name, tracked from webhooks. */
  wa_name: string | null;
  phone: string;
  email: string | null;
  region: string | null;
  optin: boolean;
  optin_source: string | null;
  orders_count: number;
  total_spend: string;
  segment: Segment;
  segment_is_manual: boolean;
  segment_auto: Segment;
  source_platform: string | null;
  last_contact_at: string | null;
}
export type ContactSort = "name" | "phone" | "segment" | "spend" | "source" | "optin";
export interface ContactFilters {
  q?: string;
  /** multi-value funnel filters compiled to IN (...) server-side */
  segment?: string[];
  platform?: string[];
  region?: string[];
  optin?: ("Yes" | "No")[];
  sort?: ContactSort;
  dir?: "asc" | "desc";
}
export interface ContactFacets {
  sources: string[];
  regions: string[];
  total: number;
  optedIn: number;
}
export interface AudienceView {
  id: number;
  label: string;
  kind: "builtin" | "filter" | "upload";
  definition: AudienceCriteria;
  count: number;
}

const PAGE = 25;

export function useContacts(businessId: string, filters: ContactFilters) {
  return useInfiniteQuery({
    queryKey: [businessId, "contacts", filters],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({
        business: businessId,
        limit: String(PAGE),
        offset: String(pageParam),
      });
      if (filters.q) p.set("q", filters.q);
      if (filters.segment?.length) p.set("segment", filters.segment.join(","));
      if (filters.platform?.length) p.set("platform", filters.platform.join(","));
      if (filters.region?.length) p.set("region", filters.region.join(","));
      if (filters.optin?.length) p.set("optin", filters.optin.includes("Yes") ? "true" : "false");
      if (filters.sort) p.set("sort", filters.sort);
      if (filters.dir) p.set("dir", filters.dir);
      return apiFetch<{ contacts: ContactRow[]; total: number }>(`/api/v1/contacts?${p}`);
    },
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, pg) => n + pg.contacts.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
  });
}

export function useFacets(businessId: string) {
  return useQuery<ContactFacets>({
    queryKey: [businessId, "contacts", "facets"],
    queryFn: () => apiFetch(`/api/v1/contacts/facets?business=${businessId}`),
  });
}

export function useThresholds(businessId: string) {
  return useQuery<{ vipSpend: number; vipOrders: number }>({
    queryKey: [businessId, "thresholds"],
    queryFn: () => apiFetch(`/api/v1/segment-thresholds?business=${businessId}`),
  });
}

export function useUpdateThresholds(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { vipSpend?: number; vipOrders?: number }) =>
      apiFetch(`/api/v1/segment-thresholds?business=${businessId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [businessId, "thresholds"] });
      qc.invalidateQueries({ queryKey: [businessId, "contacts"] });
      qc.invalidateQueries({ queryKey: [businessId, "audiences"] });
    },
  });
}

export function useAudiences(businessId: string) {
  return useQuery<AudienceView[]>({
    queryKey: [businessId, "audiences"],
    queryFn: async () => {
      const r = await apiFetch<{ audiences: AudienceView[] }>(
        `/api/v1/audiences?business=${businessId}`,
      );
      return r.audiences;
    },
  });
}

export function useCreateAudience(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { label: string; kind: "filter"; definition: AudienceCriteria }) =>
      apiFetch(`/api/v1/audiences?business=${businessId}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [businessId, "audiences"] }),
  });
}

export function useUploadAudience(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { label: string; csv: string }) =>
      apiFetch<{
        audienceId: number;
        total: number;
        valid: number;
        invalid: number;
        matched: number;
        unmatched: number;
      }>(`/api/v1/audiences/upload?business=${businessId}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [businessId, "audiences"] }),
  });
}

export interface ImportResult {
  total: number;
  valid: number;
  invalid: number;
  imported: number;
  existing: number;
}

/** Bulk CSV import — creates contacts (vs audience upload, which only matches existing). */
export function useImportContacts(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { csv: string }) =>
      apiFetch<ImportResult>(`/api/v1/contacts/import?business=${businessId}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [businessId, "contacts"] });
      qc.invalidateQueries({ queryKey: [businessId, "audiences"] });
    },
  });
}

/** Set/clear the operator's custom name (null → display falls back to the WhatsApp name). */
export function useRenameContact(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string | null }) =>
      apiFetch(`/api/v1/contacts/${id}/name`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [businessId, "contacts"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
    },
  });
}

export function useSetSegment(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, override }: { id: number; override: "new" | "repeat" | "vip" | null }) =>
      apiFetch(`/api/v1/contacts/${id}/segment`, {
        method: "PATCH",
        body: JSON.stringify({ override }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [businessId, "contacts"] });
      qc.invalidateQueries({ queryKey: [businessId, "audiences"] });
    },
  });
}
